<template>
	<div id="clientLandingPageCustomizer" class="w-100">
		<MediaLibraryModal v-on:mediaInsert="insertPhoto($event)" v-on:hideMediaModal="hideMediaModal()" />
		<DataSelectModal :type="dataSelectType" v-on:selectDataItem="selectDataItem($event)" />
		<div class="customizer-wrapper position-relative jc-customizer">
			<div
				v-if="sidebarMinimized"
				class="sidebar-unminimizer jc-customizer-unminimizer d-flex justify-content-center align-items-center position-fixed start-0 top-0 opacity-25 bg-dark cursor-pointer"
				@click="sidebarMinimize"
			>
				<i class="fa-solid fa-arrow-right"></i>
			</div>

			<!-- Customizer sidebar -->
			<div class="customizer-sidebar" :class="sidebarMinimized && 'sidebar-minimized'">
				<!-- Heading -->
				<div class="customizer-heading">
					<div class="customizer-heading__top">
						<button type="button" class="customizer-heading__collapse" @click="sidebarMinimize" aria-label="Hide customizer sidebar">
							<i class="fa-solid fa-arrow-left"></i>
						</button>
						<small class="customizer-heading__meta">You are customizing</small>
						<router-link class="customizer-back-link" :to="'/juniper/clients/edit/' + this.$route.params.id + '/landing-pages'">Back to list</router-link>
					</div>
					<h5>{{ lp.title || 'Landing Page' }}</h5>
				</div>

				<!-- Sidebar Menu -->
				<div class="sidebar-menu" v-if="sidebarMenu">
					<div class="list-group">
						<button type="button" @click="showSettings('sections')" class="list-group-item list-group-item-action customizer-nav-btn">
							Sections <small class="float-end text-muted">{{ lp.sections_json.length }}</small>
						</button>
						<button type="button" @click="showSettings('header')" class="list-group-item list-group-item-action customizer-nav-btn">Header</button>
						<button type="button" @click="showSettings('footer')" class="list-group-item list-group-item-action customizer-nav-btn">Footer</button>
						<button type="button" @click="showSettings('settings')" class="list-group-item list-group-item-action customizer-nav-btn">Settings &amp; SEO</button>
					</div>
				</div>

				<!-- ── SECTIONS PANEL ── -->
				<div v-if="activeSidebarSettings === 'sections'" class="settings-part homepage-settings">
					<div class="settings-part-header lp-sections-header">
						<button class="customizer-back-btn" @click="backToSidebarMenu()">
							<font-awesome-icon :icon="['fas', 'chevron-left']" />
						</button>
						<div class="title">
							<small>Customizing</small>
							<h5>Sections</h5>
						</div>
						<button class="lp-add-btn" @click="showAddSectionModal = true" title="Add section">
							<font-awesome-icon :icon="['fas', 'plus']" />
						</button>
					</div>
					<div class="settings-part-content">
						<div class="homepage-sections-wrapper">
							<h5>Landing sections</h5>
							<div v-if="lp.sections_json.length === 0" class="text-center mt-3 text-muted">
								No sections yet. Click <strong>+ Add</strong> to insert one.
							</div>
							<draggable v-else v-model="lp.sections_json" @change="updateSectionsOrder">
								<transition-group class="list-group">
									<div
										class="list-group-item list-group-item-action lp-section-row"
										:class="{ 'not-visible': !section.visible }"
										v-for="(section, i) in lp.sections_json"
										:key="section.instanceId"
									>
										<button @click="toggleVisibility(i)" class="lp-row-icon-btn" :title="section.visible ? 'Hide' : 'Show'">
											<font-awesome-icon v-if="section.visible" :icon="['fas', 'eye']" />
											<font-awesome-icon v-else :icon="['fas', 'eye-slash']" />
										</button>
										<span class="lp-section-label">
											{{ section.name }}
											<small v-if="instanceLabel(section)" class="text-muted">{{ instanceLabel(section) }}</small>
										</span>
										<div class="lp-row-actions">
											<button
												v-if="section.variables && section.variables.length > 0"
												class="lp-row-icon-btn lp-row-icon-btn--edit"
												@click="openSectionSettings(i)"
												title="Edit"
											>
												<font-awesome-icon :icon="['fas', 'pen']" />
											</button>
											<button
												class="lp-row-icon-btn lp-row-icon-btn--delete"
												@click="removeSection(i)"
												title="Remove"
											>
												<font-awesome-icon :icon="['fas', 'times']" />
											</button>
										</div>
									</div>
								</transition-group>
							</draggable>
							<div class="section-variables">
								<div v-for="(section, i) in lp.sections_json" :key="section.instanceId + '-vars'">
									<div v-if="section.variables && section.variables.length > 0 && section.settings" class="section-settings border mt-2 rounded-2 p-2 bg-white">
										<div class="d-flex justify-content-between align-items-center">
											<h5 class="p-0 m-0">
												{{ section.name }}<span v-if="instanceLabel(section)"> {{ instanceLabel(section) }}</span> settings
											</h5>
											<button @click="closeSectionSettings()" class="btn p-0 m-0">x</button>
										</div>
										<div class="form-group mb-2" v-for="(variable, index) in section.variables" :key="section.instanceId + '-' + variable.code + '-' + index">
											<CustomizerProperty
												:data="variable"
												:index="index"
												:googleFonts="googleFonts"
												:type="'landing-section'"
												:sectionIndex="i"
												:themeOptions="themeOptions.theme_settings_json"
												v-on:updatePreview="updatePreview($event)"
												v-on:showMediaLibraryModal="showMediaModal($event)"
												v-on:showDataSelectModal="showDataSelectModal($event)"
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- ── HEADER PANEL ── -->
				<div v-if="activeSidebarSettings === 'header'" class="settings-part header-settings">
					<div class="settings-part-header">
						<button class="customizer-back-btn" @click="backToSidebarMenu()">
							<font-awesome-icon :icon="['fas', 'chevron-left']" />
						</button>
						<div class="title">
							<small>Customizing</small>
							<h5>Header</h5>
						</div>
					</div>
					<div class="settings-part-content">
						<div class="headers-wrapper">
							<div class="d-flex justify-content-between align-items-center">
								<h6 class="p-0 m-0">Template</h6>
								<div class="form-group" style="width: 180px">
									<select @change="onHeaderTemplateChange" class="form-select" v-model="lp.header_active">
										<option v-for="(h, i) in headers" :value="h" :key="i + '-h'">{{ h }}</option>
									</select>
								</div>
							</div>

							<div
								class="mt-3"
								v-if="
									lp.header_active &&
									allHeaders[lp.header_active] &&
									allHeaders[lp.header_active].variables &&
									allHeaders[lp.header_active].variables.length > 0
								"
							>
								<h5>Header content</h5>
								<div class="form-group mb-2" v-for="(variable, index) in allHeaders[lp.header_active].variables" :key="'h-' + variable.code + '-' + index">
									<CustomizerProperty
										:data="variable"
										:index="index"
										:googleFonts="googleFonts"
										:type="'landing-header'"
										:sectionIndex="0"
										:themeOptions="themeOptions.theme_settings_json"
										v-on:updatePreview="updatePreview($event)"
										v-on:showMediaLibraryModal="showMediaModal($event)"
									/>
								</div>
							</div>
							<div v-else class="text-center mt-3">No settings for this header 😔</div>
						</div>
					</div>
				</div>

				<!-- ── FOOTER PANEL ── -->
				<div v-if="activeSidebarSettings === 'footer'" class="settings-part footer-settings">
					<div class="settings-part-header">
						<button class="customizer-back-btn" @click="backToSidebarMenu()">
							<font-awesome-icon :icon="['fas', 'chevron-left']" />
						</button>
						<div class="title">
							<small>Customizing</small>
							<h5>Footer</h5>
						</div>
					</div>
					<div class="settings-part-content">
						<div class="footer-wrapper">
							<div class="d-flex justify-content-between align-items-center">
								<h6 class="p-0 m-0">Template</h6>
								<div class="form-group" style="width: 180px">
									<select @change="onFooterTemplateChange" class="form-select" v-model="lp.footer_active">
										<option v-for="(f, i) in footers" :value="f" :key="i + '-f'">{{ f }}</option>
									</select>
								</div>
							</div>

							<div
								class="mt-3"
								v-if="
									lp.footer_active &&
									allFooters[lp.footer_active] &&
									allFooters[lp.footer_active].variables &&
									allFooters[lp.footer_active].variables.length > 0
								"
							>
								<h5>Footer content</h5>
								<div class="form-group mb-2" v-for="(variable, index) in allFooters[lp.footer_active].variables" :key="'f-' + variable.code + '-' + index">
									<CustomizerProperty
										:data="variable"
										:index="index"
										:googleFonts="googleFonts"
										:type="'landing-footer'"
										:sectionIndex="0"
										:themeOptions="themeOptions.theme_settings_json"
										v-on:updatePreview="updatePreview($event)"
										v-on:showMediaLibraryModal="showMediaModal($event)"
									/>
								</div>
							</div>
							<div v-else class="text-center mt-3">No settings for this footer 😔</div>
						</div>
					</div>
				</div>

				<!-- ── SETTINGS & SEO PANEL ── -->
				<div v-if="activeSidebarSettings === 'settings'" class="settings-part">
					<div class="settings-part-header">
						<button class="customizer-back-btn" @click="backToSidebarMenu()">
							<font-awesome-icon :icon="['fas', 'chevron-left']" />
						</button>
						<div class="title">
							<small>Customizing</small>
							<h5>Settings &amp; SEO</h5>
						</div>
					</div>
					<div class="settings-part-content">
						<div class="card">
							<div class="card-body">
								<div class="form-group mb-3">
									<label class="form-label">Status</label>
									<select class="form-select" v-model="lp.status">
										<option value="draft">Draft</option>
										<option value="publish">Published</option>
									</select>
									<small class="text-muted">
										Draft is hidden. Publishing requires a fresh
										<strong>Generate Static</strong> to appear live.
									</small>
								</div>
								<div class="form-group mb-3">
									<label class="form-label">Title</label>
									<input type="text" class="form-control" v-model="lp.title" />
								</div>
								<div class="form-group mb-3">
									<label class="form-label">Slug (URL)</label>
									<div class="input-group">
										<span class="input-group-text">/</span>
										<input type="text" class="form-control" v-model="lp.slug" @blur="checkSlug" />
										<span class="input-group-text">/</span>
									</div>
									<small v-if="slugMessage" :class="slugAvailable ? 'text-success' : 'text-danger'">
										{{ slugMessage }}
									</small>
								</div>
								<div class="form-group mb-3">
									<label class="form-label">Meta title (SEO)</label>
									<input type="text" class="form-control" v-model="lp.meta_title" />
								</div>
								<div class="form-group mb-3">
									<label class="form-label">Meta description (SEO)</label>
									<textarea class="form-control" rows="3" v-model="lp.meta_description"></textarea>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Update + Generate Static -->
				<div class="update">
					<button :disabled="updateLoading || loadingPage" @click="saveLandingPage()" class="btn btn-primary w-100 customizer-update-btn">
						<font-awesome-icon :icon="['fas', updateLoading ? 'spinner' : 'save']" :class="[updateLoading ? 'fa-spin' : '']" />
						Update
					</button>
					<button :disabled="staticLoading || loadingPage" @click="generateStatic()" class="btn btn-outline-light w-100 mt-2 customizer-secondary-btn">
						<font-awesome-icon :icon="[staticLoading ? 'fas' : 'fab', staticLoading ? 'spinner' : 'html5']" :class="[staticLoading ? 'fa-spin me-1' : 'me-1']" />
						{{ staticLoading ? 'Generating…' : 'Generate Static' }}
					</button>
					<a v-if="publicUrl" :href="publicUrl" target="_blank" rel="noopener" class="btn btn-link w-100 mt-2">
						<font-awesome-icon :icon="['fas', 'external-link-alt']" /> Open published URL
					</a>
				</div>
			</div>

			<!-- ── PREVIEW (v-html injection like homepage customizer) ── -->
			<div class="customizer-content" :class="sidebarMinimized && 'sidebar-minimized'">
				<div v-if="loadingPage" class="customizer-content-loader">
					Loading
					<font-awesome-icon :class="'fa-spin ms-2'" :icon="['fas', 'spinner']" />
				</div>
				<div class="customizer-preview-shell">
					<div v-html="'<style>' + settingsCss + '</style>'"></div>
					<div v-html="assetsCss"></div>
					<div v-html="'<style>' + themeCss + '</style>'"></div>
					<div v-html="'<style>' + fontsCss + '</style>'"></div>
					<div v-html="'<style>' + headerCss + '</style>'"></div>
					<div v-if="header && header.html" v-html="header.html"></div>
					<template v-for="section in lp.sections_json">
						<section :key="section.instanceId" :id="section.instanceId" v-if="section.visible">
							<div v-html="section.content"></div>
						</section>
					</template>
					<!-- landingCss MUST come AFTER sections so customizer overrides win against each section's inline default <style> block -->
					<div v-html="'<style>' + landingCss + '</style>'"></div>
					<div v-html="'<style>' + footerCss + '</style>'"></div>
					<div v-if="footer && footer.html" v-html="footer.html"></div>
				</div>
			</div>
		</div>

		<!-- ── ADD SECTION MODAL (v-show, no Bootstrap.Modal) ── -->
		<div v-if="showAddSectionModal" class="lp-modal-backdrop" @click.self="showAddSectionModal = false">
			<div class="lp-modal-dialog" role="dialog" aria-modal="true">
				<div class="lp-modal-content">
					<div class="lp-modal-header">
						<h5 class="modal-title mb-0">Add Section</h5>
						<button type="button" class="btn-close" @click="showAddSectionModal = false" aria-label="Close"></button>
					</div>
					<div class="lp-modal-body">
						<div
							v-for="section in availableSections"
							:key="section.name"
							class="lp-add-card"
							@click="addSection(section)"
						>
							<strong>{{ section.name }}</strong>
							<small class="text-muted d-block">{{ section.code }}</small>
						</div>
						<p v-if="availableSections.length === 0" class="text-muted">
							No section templates available in this theme.
						</p>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import juniperApi from '@/api/juniperApi'
import draggable from 'vuedraggable'
import CustomizerProperty from '@/components/juniper/customizer/CustomizerProperty'
import MediaLibraryModal from '@/components/juniper/media/MediaLibraryModal'
import DataSelectModal from '@/components/juniper/customizer/modals/DataSelectModal.vue'
import { Modal } from 'bootstrap'
import fonts from '../clients/include/fonts.json'

export default {
	name: 'ClientLandingPageCustomizer',
	components: { draggable, CustomizerProperty, MediaLibraryModal, DataSelectModal },
	data() {
		return {
			loading: false,
			loadingPage: true,
			updateLoading: false,
			staticLoading: false,
			sidebarMenu: true,
			activeSidebarSettings: '',
			sidebarMinimized: false,

			client: {},
			activeTheme: 'default',
			lp: {
				id: null,
				title: '',
				slug: '',
				status: 'draft',
				sections_json: [],
				header_active: '',
				header_json: [],
				footer_active: '',
				footer_json: [],
				meta_title: '',
				meta_description: '',
			},

			// Theme catalog
			availableSections: [],
			headers: [],
			footers: [],
			allHeaders: {},
			allFooters: {},
			header: { html: '', variables: [], js: '' },
			footer: { html: '', variables: [], js: '' },
			themeOptions: { theme_settings_json: [] },

			// CSS layers (mirroring homepage customizer)
			settingsCss: ':root{--primary-color:#00a08d;--secondary-color:#002447;--tertiary-color: #cfeae7;}',
			themeCss: '',
			assetsCss: '',
			headerCss: '',
			footerCss: '',
			landingCss: '',
			fontsCss: '',
			googleFonts: fonts,

			// Add Section modal
			showAddSectionModal: false,

			// Media + Data select modals
			$mediaModal: null,
			$dataSelectModal: null,
			mediaElement: null,
			dataSelectType: '',
			dataSelectItem: null,
			dataSelectSectionIndex: null,
			dataSelectSectionType: '',

			// Settings tab
			slugAvailable: true,
			slugMessage: '',
			publicUrl: '',
		}
	},
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']),
	},
	async created() {
		await this.attachScripts()
		await this.loadClient()
	},
	mounted() {
		this.$nextTick(() => {
			const mediaEl = document.getElementById('mediaLibraryModal')
			const dataEl = document.getElementById('dataSelectModal')
			// Move modals to <body> so the customizer-wrapper's positioning context
			// can't break their position:fixed / leave only the backdrop visible.
			if (mediaEl && mediaEl.parentNode !== document.body) {
				document.body.appendChild(mediaEl)
			}
			if (dataEl && dataEl.parentNode !== document.body) {
				document.body.appendChild(dataEl)
			}
			if (mediaEl) this.$mediaModal = new Modal(mediaEl)
			if (dataEl) this.$dataSelectModal = new Modal(dataEl)
		})
	},
	beforeDestroy() {
		if (this.$mediaModal) {
			this.$mediaModal.hide()
			this.$mediaModal.dispose()
		}
		if (this.$dataSelectModal) {
			this.$dataSelectModal.hide()
			this.$dataSelectModal.dispose()
		}
		// Clean up teleported elements so they don't linger on the next route
		const mediaEl = document.getElementById('mediaLibraryModal')
		const dataEl = document.getElementById('dataSelectModal')
		if (mediaEl && mediaEl.parentNode === document.body) {
			document.body.removeChild(mediaEl)
		}
		if (dataEl && dataEl.parentNode === document.body) {
			document.body.removeChild(dataEl)
		}
	},
	methods: {
		// ── Asset bootstrap (mirror of homepage customizer) ──
		async attachScripts() {
			await this.loadJquery()
			await this.loadMarquee()
			await this.loadFontAwesome()
			await this.loadOwl2Css()
			await this.loadOwl2Js()
			await this.loadSlickCss()
			await this.loadSlickJs()
			await this.loadSwiperCss()
			await this.loadSwiperJs()
			await this.loadMask()
		},
		loadMarquee() {
			return new Promise(resolve => {
				let s = document.createElement('script')
				s.src = 'https://cdn.jsdelivr.net/npm/jquery.marquee@1.6.0/jquery.marquee.min.js'
				s.onload = resolve
				document.head.appendChild(s)
			})
		},
		loadMask() {
			return new Promise(resolve => {
				let s = document.createElement('script')
				s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jquery.mask/0.9.0/jquery.mask.min.js'
				s.onload = resolve
				document.head.appendChild(s)
			})
		},
		loadJquery() {
			return new Promise(resolve => {
				let s = document.createElement('script')
				s.src = 'https://code.jquery.com/jquery-3.6.3.min.js'
				s.onload = resolve
				document.head.appendChild(s)
			})
		},
		loadFontAwesome() {
			return new Promise(resolve => {
				let l = document.createElement('link')
				l.rel = 'stylesheet'
				l.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.6.0/css/all.min.css'
				l.onload = resolve
				document.head.appendChild(l)
			})
		},
		loadOwl2Css() {
			return new Promise(resolve => {
				let l = document.createElement('link')
				l.rel = 'stylesheet'
				l.href = 'https://cdnjs.cloudflare.com/ajax/libs/OwlCarousel2/2.3.4/assets/owl.carousel.min.css'
				l.onload = resolve
				document.head.appendChild(l)
			})
		},
		loadOwl2Js() {
			return new Promise(resolve => {
				let s = document.createElement('script')
				s.src = 'https://cdnjs.cloudflare.com/ajax/libs/OwlCarousel2/2.3.4/owl.carousel.min.js'
				s.onload = resolve
				document.head.appendChild(s)
			})
		},
		loadSlickCss() {
			return new Promise(resolve => {
				let l = document.createElement('link')
				l.rel = 'stylesheet'
				l.href = '//cdn.jsdelivr.net/npm/slick-carousel@1.8.1/slick/slick.css'
				l.onload = resolve
				document.head.appendChild(l)
			})
		},
		loadSlickJs() {
			return new Promise(resolve => {
				let s = document.createElement('script')
				s.src = '//cdn.jsdelivr.net/npm/slick-carousel@1.8.1/slick/slick.min.js'
				s.onload = resolve
				document.head.appendChild(s)
			})
		},
		loadSwiperCss() {
			return new Promise(resolve => {
				let l = document.createElement('link')
				l.rel = 'stylesheet'
				l.href = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css'
				l.onload = resolve
				document.head.appendChild(l)
			})
		},
		loadSwiperJs() {
			return new Promise(resolve => {
				let s = document.createElement('script')
				s.src = 'https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js'
				s.onload = resolve
				document.head.appendChild(s)
			})
		},
		// Run header/footer/section JS AFTER Vue has flushed the v-html update
		// to the DOM. Without nextTick + small delay, jQuery selectors (e.g.,
		// $('#tickerBar').marquee()) run before the element exists and silently
		// no-op, leaving the tickerbar invisible.
		executeScript(script) {
			if (!script) {
				this.loadingPage = false
				return
			}
			this.$nextTick(() => {
				// extra rAF to ensure layout is committed before plugins measure
				requestAnimationFrame(() => {
					try {
						eval(script)
					} catch (e) {
						console.warn('Script execution failed', e)
					}
					this.loadingPage = false
				})
			})
		},

		// ── Initial load ──
		async loadClient() {
			try {
				const { data } = await juniperApi.get('/clients/' + this.$route.params.id)
				this.client = data
				this.activeTheme = this.resolveTheme(data)
				await this.loadLp()
				await Promise.all([
					this.getThemeCss(),
					this.getThemeAssetsCss(),
					this.getThemeSettings(),
					this.getAvailableSections(),
					this.getThemeHeadersList(),
					this.getThemeFootersList(),
					this.getHeader(),
					this.getFooter(),
				])
				this.computePublicUrl()
				this.loadingPage = false
			} catch (e) {
				console.error(e)
				this.loadingPage = false
			}
		},
		resolveTheme(client) {
			if (client && client.options) {
				const opt = client.options.find(o => o.meta_key === 'theme_active')
				if (opt) return opt.meta_value
			}
			return client?.theme_active || client?.theme || 'default'
		},

		// ── LP record ──
		async loadLp() {
			const { data } = await juniperApi.get('/landing-pages/' + this.$route.params.lpId, { params: { context: 'edit' } })
			const sections = (Array.isArray(data.sections_json) ? data.sections_json : []).map(s => ({
				...s,
				instanceId: s.instanceId || this.generateInstanceId(),
				content: '', // will be populated by section preview render
				settings: false,
			}))
			this.lp = {
				...this.lp,
				...data,
				sections_json: sections,
				header_json: Array.isArray(data.header_json) ? data.header_json : [],
				footer_json: Array.isArray(data.footer_json) ? data.footer_json : [],
			}
			// Render sections content via preview endpoint
			for (const s of this.lp.sections_json) {
				await this.renderSectionContent(s)
			}
			this.updateLandingSectionsCss()
		},
		computePublicUrl() {
			if (this.lp.status !== 'publish' || !this.client) {
				this.publicUrl = ''
				return
			}
			// Prefer staging URL when client is on staging
			let base
			if (this.client.staging && this.client.staging_url) {
				base = this.client.staging_url
			} else if (this.client.url) {
				base = this.client.url
			} else {
				this.publicUrl = ''
				return
			}
			if (!base.startsWith('http')) base = 'https://' + base
			this.publicUrl = `${base.replace(/\/$/, '')}/${this.lp.slug}/`
		},

		// ── Theme CSS / assets ──
		getThemeCss() {
			return juniperApi.post('/themes/' + this.activeTheme + '/css').then(({ data }) => {
				this.themeCss = data
			})
		},
		getThemeAssetsCss() {
			return juniperApi.post('/themes/' + this.activeTheme + '/assets').then(({ data }) => {
				this.assetsCss = data
				const regex = /custom-fonts(.*?)custom-fonts/
				const match = data.match(regex)
				if (match) {
					try {
						const arr = JSON.parse(match[1])
						this.googleFonts = [...new Set(this.googleFonts.concat(arr).sort())]
					} catch (e) {
						/* ignore */
					}
				}
			})
		},
		getThemeSettings() {
			return juniperApi.get(`/themes/${this.activeTheme}/${this.$route.params.id}/theme-settings`).then(({ data }) => {
				this.themeOptions.theme_settings_json = data
				this.updateThemeSettingsPreview()
			})
		},
		updateThemeSettingsPreview() {
			let css = ':root{'
			let importFont = ''
			let figmaVariables = ''
			if (this.themeOptions && this.themeOptions.theme_settings_json) {
				this.themeOptions.theme_settings_json.forEach(setting => {
					const variableName = setting.code.toLowerCase().replaceAll('_', '-')
					const fontName = typeof setting.value === 'string' ? setting.value.replace(' ', '+') : null
					if (setting && setting.value && variableName.includes('font') && setting.value !== '' && !fontName.startsWith('TownWeb ') && !fontName.endsWith(' TownWeb')) {
						importFont +=
							"@import url('https://fonts.googleapis.com/css?family=" +
							fontName +
							":ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap');\n"
					}
					if (setting.code === 'FIGMA_VARIABLES') {
						figmaVariables = setting.value
					} else {
						css += '--' + variableName + ':' + setting.value + ';'
					}
				})
			}
			css += '}'
			this.settingsCss = importFont + figmaVariables + css
		},

		// ── Sections catalog ──
		getAvailableSections() {
			return juniperApi
				.post(`/themes/${this.activeTheme}/landing/sections`, { client_id: this.$route.params.id })
				.then(({ data }) => {
					this.availableSections = data
				})
				.catch(() => {
					this.availableSections = []
				})
		},

		// ── Header/Footer catalogs ──
		getThemeHeadersList() {
			return juniperApi.post('/themes/' + this.activeTheme + '/headers').then(({ data }) => {
				this.headers = data
			})
		},
		getThemeFootersList() {
			return juniperApi.post('/themes/' + this.activeTheme + '/footers').then(({ data }) => {
				this.footers = data
			})
		},
		getHeader() {
			return juniperApi
				.post(`/themes/${this.activeTheme}/landing/header-data`, {
					client_id: this.$route.params.id,
					landing_page_id: this.$route.params.lpId,
				})
				.then(({ data }) => {
					this.allHeaders = data
					if (!this.lp.header_active && this.headers.length > 0) {
						this.lp.header_active = this.headers[0]
					}
					if (this.lp.header_active && data[this.lp.header_active]) {
						this.header = data[this.lp.header_active]
						this.updateHeaderCss(this.header)
						if (this.header.js) this.executeScript(this.header.js)
					}
				})
				.catch(err => console.error('getHeader failed', err))
		},
		getFooter() {
			return juniperApi
				.post(`/themes/${this.activeTheme}/landing/footer-data`, {
					client_id: this.$route.params.id,
					landing_page_id: this.$route.params.lpId,
				})
				.then(({ data }) => {
					this.allFooters = data
					if (!this.lp.footer_active && this.footers.length > 0) {
						this.lp.footer_active = this.footers[0]
					}
					if (this.lp.footer_active && data[this.lp.footer_active]) {
						this.footer = data[this.lp.footer_active]
						this.updateFooterCss(this.footer)
					}
				})
				.catch(err => console.error('getFooter failed', err))
		},
		onHeaderTemplateChange() {
			if (this.allHeaders[this.lp.header_active]) {
				this.header = this.allHeaders[this.lp.header_active]
				this.lp.header_json = this.header.variables || []
				this.updateHeaderCss(this.header)
				// $nextTick + executeScript already deferred — marquee/owl/etc
				// will re-init against the new header DOM.
				if (this.header.js) this.executeScript(this.header.js)
			}
		},
		onFooterTemplateChange() {
			if (this.allFooters[this.lp.footer_active]) {
				this.footer = this.allFooters[this.lp.footer_active]
				this.lp.footer_json = this.footer.variables || []
				this.updateFooterCss(this.footer)
			}
		},

		// ── CSS recompute (mirror of homepage customizer) ──
		updateHeaderCss(header) {
			let css = ''
			;(header.variables || []).forEach(variable => {
				if (variable.name.includes('_CSS')) {
					const cssAttributeName = variable.name.toLowerCase().substring(0, variable.name.indexOf('_'))
					const cssAttributeValue = variable.value
					let cssAttributeSelector = variable.selector || 'header'
					if (cssAttributeValue && cssAttributeValue !== '') {
						if (cssAttributeName === 'font-family' && !cssAttributeValue.startsWith('TownWeb ') && !cssAttributeValue.endsWith(' TownWeb')) {
							this.fontsCss += `@import url('https://fonts.googleapis.com/css?family=${cssAttributeValue}&display=swap');\n`
							css += `${cssAttributeSelector} {\n ${cssAttributeName}:'${cssAttributeValue}';\n }\n`
						} else if (cssAttributeSelector.includes('``@media (')) {
							cssAttributeSelector = cssAttributeSelector.replace('``', '')
							const arr = cssAttributeSelector.split('``')
							css += `${arr[0]} {\n ${arr[1]} {\n ${cssAttributeName}:${cssAttributeValue};\n }\n }\n`
						} else {
							css += `${cssAttributeSelector} {\n ${cssAttributeName}:${cssAttributeValue};\n }\n`
						}
					}
				}
			})
			this.headerCss = css
		},
		updateFooterCss(footer) {
			let css = ''
			;(footer.variables || []).forEach(variable => {
				if (variable.name.includes('_CSS')) {
					const cssAttributeName = variable.name.toLowerCase().substring(0, variable.name.indexOf('_'))
					const cssAttributeValue = variable.value
					const cssAttributeSelector = variable.selector || 'footer'
					if (cssAttributeValue && cssAttributeValue !== '') {
						if (cssAttributeName === 'font-family' && !cssAttributeValue.startsWith('TownWeb ') && !cssAttributeValue.endsWith(' TownWeb')) {
							if (!this.fontsCss.includes(cssAttributeValue)) {
								this.fontsCss += `@import url('https://fonts.googleapis.com/css?family=${cssAttributeValue}&display=swap');\n`
							}
							css += `${cssAttributeSelector} {\n ${cssAttributeName}:'${cssAttributeValue}';\n }\n`
						} else {
							css += `${cssAttributeSelector} {\n ${cssAttributeName}:${cssAttributeValue};\n }\n`
						}
					}
				}
			})
			this.footerCss = css
		},
		updateLandingSectionsCss() {
			let css = ''
			this.lp.sections_json.forEach(section => {
				const suffix = section.instanceId ? '_' + section.instanceId.replace(/[^a-zA-Z0-9_]/g, '') : ''
				;(section.variables || []).forEach(variable => {
					if (section.visible && variable.name.includes('_CSS')) {
						const cssAttributeName = variable.name.toLowerCase().substring(0, variable.name.indexOf('_'))
						const cssAttributeValue = variable.value
						let cssAttributeSelector = variable.selector || 'section'
						cssAttributeSelector = cssAttributeSelector.split('{#SECTION_INSTANCE_SUFFIX#}').join(suffix)
						if (cssAttributeValue && cssAttributeValue !== '') {
							if (cssAttributeName === 'font-family' && !cssAttributeValue.startsWith('TownWeb ') && !cssAttributeValue.endsWith(' TownWeb')) {
								if (!this.fontsCss.includes(cssAttributeValue)) {
									this.fontsCss += `@import url('https://fonts.googleapis.com/css?family=${cssAttributeValue}&display=swap');\n`
								}
								css += `${cssAttributeSelector} {\n ${cssAttributeName}:'${cssAttributeValue}';\n }\n`
							} else if (cssAttributeSelector.includes('``@media (')) {
								cssAttributeSelector = cssAttributeSelector.replace('``', '')
								const arr = cssAttributeSelector.split('``')
								css += `${arr[0]} {\n ${arr[1]} {\n ${cssAttributeName}:${cssAttributeValue};\n }\n }\n`
							} else {
								css += `${cssAttributeSelector} {\n ${cssAttributeName}:${cssAttributeValue};\n }\n`
							}
						}
					}
				})
			})
			this.landingCss = css
		},

		// ── Update preview (events from CustomizerProperty) ──
		updatePreview(e) {
			if (e.type === 'landing-header') {
				this.updateHeaderPreview()
			}
			if (e.type === 'landing-footer') {
				this.updateFooterPreview()
			}
			if (e.type === 'landing-section') {
				const section = this.lp.sections_json[e.sectionIndex]
				if (section) this.renderSectionContent(section)
			}
		},
		updateHeaderPreview() {
			juniperApi
				.post(`/themes/${this.activeTheme}/landing/update-header-preview`, {
					variables: this.header.variables,
					header: this.lp.header_active,
					clientId: this.client.id,
				})
				.then(({ data }) => {
					this.header.html = data
					this.updateHeaderCss(this.header)
					this.lp.header_json = this.header.variables
					// Re-run header JS so marquee, owls, and other plugins
					// re-attach to the freshly-rendered DOM.
					if (this.header.js) this.executeScript(this.header.js)
				})
		},
		updateFooterPreview() {
			juniperApi
				.post(`/themes/${this.activeTheme}/landing/update-footer-preview`, {
					variables: this.footer.variables,
					footer: this.lp.footer_active,
					clientId: this.client.id,
				})
				.then(({ data }) => {
					this.footer.html = data
					this.updateFooterCss(this.footer)
					this.lp.footer_json = this.footer.variables
				})
		},
		renderSectionContent(section) {
			return juniperApi
				.post(`/themes/${this.activeTheme}/landing/update-section-preview`, {
					variables: section.variables,
					section: section.name,
					clientId: this.client.id,
					instance_id: section.instanceId,
				})
				.then(({ data }) => {
					section.content = data
					this.updateLandingSectionsCss()
					// v-html doesn't execute <script> tags; run them manually so
					// inline section JS (owl carousels, custom init) wires up.
					this.$nextTick(() => this.runSectionScripts(section))
				})
				.catch(err => console.error('section render failed', err))
		},
		// Find <script> tags inside a freshly-rendered section and execute them.
		// We scope the search to the section's wrapping <section :id="instanceId">
		// so we don't accidentally re-run scripts from sibling sections.
		runSectionScripts(section) {
			if (!section || !section.instanceId) return
			const container = document.getElementById(section.instanceId)
			if (!container) return
			const scripts = container.querySelectorAll('script')
			scripts.forEach(oldScript => {
				try {
					const code = (oldScript.textContent || '').trim()
					if (code) new Function(code)()
				} catch (e) {
					console.warn('Section script error in ' + section.name, e)
				}
			})
		},

		// ── Sections (multi-instance) ──
		generateInstanceId() {
			return 'lpsec_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8)
		},
		instanceLabel(section) {
			if (!section) return ''
			const same = (this.lp.sections_json || []).filter(s => s.name === section.name)
			if (same.length < 2) return ''
			return '#' + (same.indexOf(section) + 1)
		},
		updateSectionsOrder() {
			this.lp.sections_json.forEach((s, i) => {
				s.order = (i + 1) * 10
			})
		},
		toggleVisibility(idx) {
			this.lp.sections_json[idx].visible = !this.lp.sections_json[idx].visible
			this.updateLandingSectionsCss()
		},
		openSectionSettings(idx) {
			this.lp.sections_json.forEach(s => (s.settings = false))
			this.lp.sections_json[idx].settings = true
		},
		closeSectionSettings() {
			this.lp.sections_json.forEach(s => (s.settings = false))
		},
		addSection(template) {
			const variables = JSON.parse(JSON.stringify(template.variables || []))
			const instance = {
				instanceId: this.generateInstanceId(),
				name: template.name,
				code: template.code,
				variables,
				visible: true,
				settings: false,
				order: (this.lp.sections_json.length + 1) * 10,
				content: '',
			}
			this.lp.sections_json.push(instance)
			this.showAddSectionModal = false
			this.renderSectionContent(instance)
		},
		removeSection(idx) {
			this.lp.sections_json.splice(idx, 1)
			this.updateLandingSectionsCss()
		},

		// ── Settings tab — slug check ──
		checkSlug() {
			if (!this.lp.slug) return
			juniperApi
				.get('/landing-pages/check-slug', {
					params: {
						client_id: this.$route.params.id,
						slug: this.lp.slug,
						ignore_id: this.$route.params.lpId,
					},
				})
				.then(({ data }) => {
					this.slugAvailable = data.available
					this.slugMessage = data.available
						? 'Slug is available.'
						: data.reason === 'reserved'
						? 'Reserved by the system.'
						: data.reason === 'page_collision'
						? 'A regular page already uses this slug.'
						: data.reason === 'lp_collision'
						? 'Another landing page already uses this slug.'
						: 'Slug is not available.'
				})
		},

		// ── Sidebar nav ──
		showSettings(type) {
			this.activeSidebarSettings = type
			this.sidebarMenu = false
		},
		backToSidebarMenu() {
			this.sidebarMenu = true
			this.activeSidebarSettings = ''
		},
		sidebarMinimize() {
			this.sidebarMinimized = !this.sidebarMinimized
		},

		// ── Save / Publish ──
		// `silent` skips the loading flag toggle and the success toast — used by
		// generateStatic() so it doesn't briefly flash the Update spinner before
		// the static one takes over.
		saveLandingPage(silent = false) {
			if (!silent) this.updateLoading = true
			// strip transient props from sections before saving
			const sectionsToSave = this.lp.sections_json.map(s => {
				const copy = { ...s }
				delete copy.content
				delete copy.settings
				return copy
			})
			return juniperApi
				.put(`/landing-pages/${this.$route.params.lpId}`, {
					title: this.lp.title,
					slug: this.lp.slug,
					status: this.lp.status,
					sections_json: sectionsToSave,
					header_active: this.lp.header_active,
					header_json: this.lp.header_json,
					footer_active: this.lp.footer_active,
					footer_json: this.lp.footer_json,
					meta_title: this.lp.meta_title,
					meta_description: this.lp.meta_description,
				})
				.then(({ data }) => {
					this.lp.updated_at = data.updated_at
					if (!silent) {
						this.$swal.fire({ icon: 'success', title: 'Landing page saved', timer: 2000 })
					}
				})
				.catch(err => {
					if (!silent) {
						this.$swal.fire({
							icon: 'error',
							title: 'Save failed',
							text: err.response?.data?.errors
								? Object.values(err.response.data.errors).flat().join(' ')
								: 'Could not save changes.',
						})
					} else {
						// rethrow so the caller (generateStatic) can show its own error
						throw err
					}
				})
				.finally(() => {
					if (!silent) this.updateLoading = false
				})
		},
		// Generate static content. Mirrors ClientStatic.vue pattern:
		// no confirm dialog — click triggers loader directly, save first
		// (silent — does not toggle updateLoading), then deploy, then toast.
		async generateStatic() {
			this.staticLoading = true
			try {
				await this.saveLandingPage(/* silent */ true)
				const { data } = await juniperApi.post(
					`/clients/${this.$route.params.id}/landing-pages/${this.$route.params.lpId}/static/generate`
				)
				this.lp.status = 'publish'
				this.publicUrl = data.url
				this.$swal.fire({
					icon: 'success',
					title: 'Landing page published',
					text: data.url,
					timer: 4000,
				})
			} catch (err) {
				this.$swal.fire({
					icon: 'error',
					title: 'Publish failed',
					text: err.response?.data?.error || err.message,
				})
			} finally {
				this.staticLoading = false
			}
		},

		// ── Media chooser ──
		showMediaModal(e) {
			this.mediaElement = e
			if (this.$mediaModal) this.$mediaModal.show()
		},
		insertPhoto(photoUrl) {
			if (this.mediaElement) {
				this.mediaElement.value = photoUrl
				// Re-render the affected section/header/footer preview so the new image shows
				if (this.activeSidebarSettings === 'sections') {
					const idx = this.lp.sections_json.findIndex(s => s.settings)
					if (idx >= 0) this.renderSectionContent(this.lp.sections_json[idx])
				} else if (this.activeSidebarSettings === 'header') {
					this.updateHeaderPreview()
				} else if (this.activeSidebarSettings === 'footer') {
					this.updateFooterPreview()
				}
			}
			if (this.$mediaModal) this.$mediaModal.hide()
		},
		hideMediaModal() {
			if (this.$mediaModal) this.$mediaModal.hide()
		},

		// ── Data select chooser ──
		showDataSelectModal(e) {
			this.dataSelectType = e.type
			this.dataSelectItem = e.item
			this.dataSelectSectionIndex = e.sectionIndex
			this.dataSelectSectionType = e.sectionType
			if (this.$dataSelectModal) this.$dataSelectModal.show()
		},
		selectDataItem(e) {
			if (this.dataSelectItem) {
				this.dataSelectItem.forEach(item => {
					if (e[item.name.toLowerCase()]) {
						item.value = e[item.name.toLowerCase()]
					}
				})
				this.updatePreview({
					type: this.dataSelectSectionType,
					sectionIndex: this.dataSelectSectionIndex,
				})
			}
			this.hideDataSelectModal()
		},
		hideDataSelectModal() {
			if (this.$dataSelectModal) this.$dataSelectModal.hide()
		},
	},
}
</script>

<style scoped>
/* Section row — clean horizontal layout */
.lp-section-row {
	display: flex !important;
	align-items: center;
	gap: 10px;
	padding: 10px 12px;
}
.lp-section-row.not-visible {
	opacity: 0.55;
}
.lp-section-row.not-visible .lp-section-label {
	text-decoration: line-through;
}
.lp-section-label {
	flex: 1;
	font-weight: 500;
}
.lp-row-actions {
	display: inline-flex;
	gap: 4px;
}
.lp-row-icon-btn {
	width: 30px;
	height: 30px;
	border-radius: 6px;
	border: 1px solid transparent;
	background: transparent;
	color: #6b7280;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	font-size: 13px;
	padding: 0;
	transition: all 0.15s ease;
}
.lp-row-icon-btn:hover {
	background: #f3f4f6;
	color: #111827;
}
.lp-row-icon-btn--edit:hover {
	background: #dbeafe;
	color: #1d4ed8;
}
.lp-row-icon-btn--delete:hover {
	background: #fee2e2;
	color: #dc2626;
}

/* Sections panel header — compact icon-only Add button */
.lp-sections-header {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: nowrap;
	min-width: 0;
}
.lp-sections-header .title {
	min-width: 0;
	overflow: hidden;
}
.lp-add-btn {
	margin-left: auto;
	flex-shrink: 0;
	width: 32px;
	height: 32px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border-radius: 6px;
	background: #3b82f6;
	color: white;
	border: none;
	font-size: 13px;
	cursor: pointer;
	transition: background 0.15s ease;
}
.lp-add-btn:hover {
	background: #2563eb;
}

/* Self-contained Add Section modal */
.lp-modal-backdrop {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.5);
	z-index: 1060;
	display: flex;
	align-items: flex-start;
	justify-content: center;
	padding: 80px 16px 16px;
	overflow-y: auto;
}
.lp-modal-dialog {
	width: 100%;
	max-width: 500px;
}
.lp-modal-content {
	background: #fff;
	border-radius: 8px;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
}
.lp-modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 16px 20px;
	border-bottom: 1px solid #e5e7eb;
}
.lp-modal-body {
	padding: 16px 20px;
}
.lp-add-card {
	border: 1px solid #e2e8f0;
	border-radius: 6px;
	padding: 10px 12px;
	margin-bottom: 8px;
	cursor: pointer;
	transition: all 0.15s;
}
.lp-add-card:hover {
	border-color: #3b82f6;
	background: #f9fbff;
}
</style>
